
import { useState, useEffect, useRef } from 'react';
import type { FieldData } from '../types';

interface MapViewerProps {
  fieldData: FieldData;
  onFieldUpdated: (data: FieldData) => void;
}

declare global {
  interface Window {
    google: any;
    initMap?: () => void;
  }
}

export default function MapViewer({ fieldData, onFieldUpdated }: MapViewerProps) {
  const [acreage, setAcreage] = useState<string>(fieldData.acreage.toString());
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestionApplied, setSuggestionApplied] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const polygonRef = useRef<any>(null);
  const drawingManagerRef = useRef<any>(null);

  const hasSuggestion =
    fieldData.suggestedAcreage !== undefined && fieldData.suggestedAcreage > 0;

  useEffect(() => {
    setAcreage(fieldData.acreage.toString());
  }, [fieldData.acreage]);

  // Initialize Google Maps
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&libraries=drawing,geometry`;
        script.async = true;
        script.defer = true;
        script.onload = initializeMap;
        document.head.appendChild(script);
      }
    };

    loadGoogleMaps();
  }, []);

  // Update map when field data changes
  useEffect(() => {
    if (mapInstanceRef.current && fieldData) {
      updateMapLocation();
    }
  }, [fieldData.latitude, fieldData.longitude]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    const center = {
      lat: fieldData.latitude,
      lng: fieldData.longitude,
    };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 17,
      mapTypeId: 'satellite',
      tilt: 0,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_RIGHT,
      },
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    // Add marker
    const marker = new window.google.maps.Marker({
      position: center,
      map,
      draggable: true,
      title: 'Field Location',
    });

    markerRef.current = marker;

    // Update location when marker is dragged
    marker.addListener('dragend', () => {
      const position = marker.getPosition();
      onFieldUpdated({
        ...fieldData,
        latitude: position.lat(),
        longitude: position.lng(),
      });
    });

    // Initialize Drawing Manager
    const drawingManager = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: window.google.maps.ControlPosition.TOP_CENTER,
        drawingModes: ['polygon'],
      },
      polygonOptions: {
        fillColor: '#10b981',
        fillOpacity: 0.3,
        strokeWeight: 2,
        strokeColor: '#059669',
        editable: true,
        draggable: true,
      },
    });

    drawingManager.setMap(map);
    drawingManagerRef.current = drawingManager;

    // Handle polygon completion
    window.google.maps.event.addListener(
      drawingManager,
      'polygoncomplete',
      (polygon: any) => {
        // Remove old polygon if exists
        if (polygonRef.current) {
          polygonRef.current.setMap(null);
        }

        polygonRef.current = polygon;
        setIsDrawing(false);
        drawingManager.setDrawingMode(null);

        // Calculate acreage from drawn polygon
        const calculatedAcreage = calculatePolygonAcreage(polygon);
        setAcreage(calculatedAcreage.toString());

        // Update field data with new polygon
        const path = polygon.getPath();
        const coordinates: Array<{ lat: number; lng: number }> = [];
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          coordinates.push({ lat: point.lat(), lng: point.lng() });
        }

        onFieldUpdated({
          ...fieldData,
          acreage: calculatedAcreage,
          polygon: coordinates,
          boundarySource: 'manual',
        });

        // Listen for polygon edits
        window.google.maps.event.addListener(path, 'set_at', () => {
          updatePolygonAcreage(polygon);
        });
        window.google.maps.event.addListener(path, 'insert_at', () => {
          updatePolygonAcreage(polygon);
        });
      }
    );

    // Draw existing polygon if available
    if (fieldData.polygon && fieldData.polygon.length > 0) {
      drawExistingPolygon(map, fieldData.polygon);
    }
  };

  const updateMapLocation = () => {
    if (!mapInstanceRef.current || !markerRef.current) return;

    const newCenter = {
      lat: fieldData.latitude,
      lng: fieldData.longitude,
    };

    mapInstanceRef.current.setCenter(newCenter);
    markerRef.current.setPosition(newCenter);
  };

  const drawExistingPolygon = (map: any, coordinates: Array<{ lat: number; lng: number }>) => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
    }

    const polygon = new window.google.maps.Polygon({
      paths: coordinates,
      fillColor: '#10b981',
      fillOpacity: 0.3,
      strokeWeight: 2,
      strokeColor: '#059669',
      editable: true,
      draggable: true,
    });

    polygon.setMap(map);
    polygonRef.current = polygon;

    // Listen for polygon edits
    const path = polygon.getPath();
    window.google.maps.event.addListener(path, 'set_at', () => {
      updatePolygonAcreage(polygon);
    });
    window.google.maps.event.addListener(path, 'insert_at', () => {
      updatePolygonAcreage(polygon);
    });
  };

  const calculatePolygonAcreage = (polygon: any): number => {
    const area = window.google.maps.geometry.spherical.computeArea(
      polygon.getPath()
    );
    const acres = area / 4046.86; // Convert square meters to acres
    return Math.round(acres * 10) / 10;
  };

  const updatePolygonAcreage = (polygon: any) => {
    const calculatedAcreage = calculatePolygonAcreage(polygon);
    setAcreage(calculatedAcreage.toString());

    const path = polygon.getPath();
    const coordinates: Array<{ lat: number; lng: number }> = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coordinates.push({ lat: point.lat(), lng: point.lng() });
    }

    onFieldUpdated({
      ...fieldData,
      acreage: calculatedAcreage,
      polygon: coordinates,
      boundarySource: 'manual',
    });
  };

  const handleStartDrawing = () => {
    if (drawingManagerRef.current) {
      setIsDrawing(true);
      drawingManagerRef.current.setDrawingMode(
        window.google.maps.drawing.OverlayType.POLYGON
      );
    }
  };

  const handleClearPolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
      setIsDrawing(false);
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setDrawingMode(null);
      }
    }
  };

  const handleConfirmAcreage = () => {
    const parsed = parseFloat(acreage);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    onFieldUpdated({
      ...fieldData,
      acreage: Math.round(parsed * 10) / 10,
      boundarySource: 'manual',
    });
    setHasConfirmed(true);
  };

  const handleApplySuggestion = () => {
    if (!fieldData.suggestedAcreage) return;
    const suggested = Math.round(fieldData.suggestedAcreage * 10) / 10;
    setAcreage(suggested.toString());
    onFieldUpdated({
      ...fieldData,
      acreage: suggested,
      polygon: fieldData.suggestedPolygon || fieldData.polygon,
      boundarySource: 'auto',
    });
    setSuggestionApplied(true);
    setHasConfirmed(true);
    setShowSuggestion(false);

    // Draw suggested polygon on map
    if (
      fieldData.suggestedPolygon &&
      fieldData.suggestedPolygon.length > 0 &&
      mapInstanceRef.current
    ) {
      drawExistingPolygon(mapInstanceRef.current, fieldData.suggestedPolygon);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      {/* Map Container */}
      <div className="relative w-full h-[500px] bg-gray-100">
        <div ref={mapRef} className="w-full h-full"></div>

        {/* Overlay Instructions */}
        <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-pencil-ruler-2-line text-emerald-700 text-lg"></i>
            </div>
            <div className="text-sm">
              <p className="font-medium text-gray-900 mb-1">
                Draw Your Field Boundary
              </p>
              <p className="text-gray-600 mb-2">
                Drag the pin to adjust location. Use the polygon tool to outline your field, or enter acreage manually below.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleStartDrawing}
                  disabled={isDrawing}
                  className="px-3 py-1.5 bg-emerald-700 text-white text-xs font-medium rounded-md hover:bg-emerald-800 transition-colors cursor-pointer whitespace-nowrap disabled:bg-gray-400"
                >
                  <i className="ri-shape-line mr-1"></i>
                  Draw Field
                </button>
                {polygonRef.current && (
                  <button
                    onClick={handleClearPolygon}
                    className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Coordinates Badge */}
        <div className="absolute bottom-4 left-4 bg-gray-900/80 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-medium">
          <i className="ri-map-pin-line mr-2"></i>
          {fieldData.latitude.toFixed(5)}, {fieldData.longitude.toFixed(5)}
        </div>
      </div>

      {/* Field Info Panel */}
      <div className="p-6 border-t border-gray-200">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Location Info */}
          <div>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-line text-emerald-700"></i>
              </div>
              Field Location
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-500">Address:</span>
                <p className="font-medium text-gray-900">{fieldData.address}</p>
              </div>
              <div>
                <span className="text-gray-500">Coordinates:</span>
                <p className="font-mono text-xs text-gray-700">
                  {fieldData.latitude.toFixed(6)}, {fieldData.longitude.toFixed(6)}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Boundary Method:</span>
                <p className="font-medium text-gray-900">
                  {fieldData.boundarySource === 'auto'
                    ? 'Suggestion applied (review recommended)'
                    : 'Manual entry'}
                </p>
              </div>
            </div>
          </div>

          {/* Acreage Entry — Primary */}
          <div>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-ruler-line text-emerald-700"></i>
              </div>
              Field Size
            </h3>

            <label className="block text-sm text-gray-600 mb-2">
              Enter your field acreage:
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={acreage}
                onChange={(e) => {
                  setAcreage(e.target.value);
                  setHasConfirmed(false);
                  setSuggestionApplied(false);
                }}
                step="0.1"
                min="0.1"
                placeholder="e.g., 120"
                className="w-36 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-lg font-semibold"
              />
              <span className="text-gray-600 font-medium">acres</span>
            </div>

            {!hasConfirmed && (
              <button
                onClick={handleConfirmAcreage}
                disabled={!acreage || parseFloat(acreage) <= 0}
                className="mt-3 px-5 py-2 bg-emerald-800 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Confirm Acreage
              </button>
            )}

            {hasConfirmed && (
              <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 font-medium">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line"></i>
                </div>
                Acreage confirmed
                <button
                  onClick={() => setHasConfirmed(false)}
                  className="text-gray-500 hover:text-gray-700 underline ml-1 cursor-pointer whitespace-nowrap"
                >
                  Edit
                </button>
              </div>
            )}

            {/* Suggestion Banner */}
            {hasSuggestion && !suggestionApplied && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-lightbulb-line text-amber-600 text-sm"></i>
                  </div>
                  <div className="text-xs text-amber-900">
                    <p className="font-medium mb-1">
                      Auto-detected suggestion available
                    </p>
                    <p className="text-amber-800 mb-2">
                      Our system estimated approximately{' '}
                      <strong>
                        {Math.round(fieldData.suggestedAcreage! * 10) / 10} acres
                      </strong>{' '}
                      from satellite imagery. This is an estimate only and may not
                      be accurate.
                    </p>
                    {!showSuggestion ? (
                      <button
                        onClick={() => setShowSuggestion(true)}
                        className="text-amber-700 hover:text-amber-900 font-medium underline cursor-pointer whitespace-nowrap"
                      >
                        Use this suggestion
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={handleApplySuggestion}
                          className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-md hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Apply{' '}
                          {Math.round(fieldData.suggestedAcreage! * 10) / 10}{' '}
                          acres
                        </button>
                        <button
                          onClick={() => setShowSuggestion(false)}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          No thanks
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {suggestionApplied && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-xs text-emerald-900">
                  <i className="ri-information-line mr-1"></i>
                  Suggestion applied. We recommend verifying this against your
                  own records — auto-detected boundaries are approximate.
                </p>
              </div>
            )}

            {!hasSuggestion && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-600">
                  <i className="ri-information-line mr-1"></i>
                  No auto-detected boundary available for this location. Please
                  enter your acreage manually using your farm records or FSA maps.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
